import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { resizeImage } from "../Sites/SiteAboutPage/SiteAboutPageUtils/resizeImage";
import { LineCrossingsOverlay } from "../Sites/SiteAboutPage/LineCrossingsOverlay";
import { backendToFrontendDirection } from "../EditLineCrossings/editLineCrossingsStore/editLineCrossingsStore";
import { normalizeBackendDirection } from "../EditLineCrossings/EditLineCrossingsUtils/lineDirection";
import { LINE_COLORS } from "../EditLineCrossings/EditLineCrossingsUtils/lineColors";
import { useCameraPreviewUrl } from "./EditCameraUtils/useCameraPreviewUrl";
import { HistoryCoordinates } from "./HistoryCoordinates";

/** A line crossing as it stood at one action-log history record. */
export interface LineCrossingHistorySnapshot {
  id: number;
  coordinates: [[number, number], [number, number]];
  direction: string | null;
  preset: number | null;
}

const DIRECTION_LABELS = {
  l2r: "Left to right",
  r2l: "Right to left",
  both: "Both",
};

export const LineCrossingPreviewDialog = ({
  open,
  onClose,
  cameraId,
  lineCrossing,
}: {
  open: boolean;
  onClose: () => void;
  cameraId: string;
  lineCrossing: LineCrossingHistorySnapshot | null;
}) => {
  const { previewUrl, loading, error } = useCameraPreviewUrl(open, cameraId);
  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);

  React.useEffect(() => {
    if (open) setDimensions(null);
  }, [open, cameraId]);

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    setDimensions(resizeImage(naturalWidth, naturalHeight));
  };

  const line =
    lineCrossing &&
    Array.isArray(lineCrossing.coordinates) &&
    lineCrossing.coordinates.length === 2
      ? lineCrossing
      : null;

  const overlayLines = line
    ? [
        {
          id: line.id,
          camera: Number(cameraId),
          metric_labels: [],
          coordinates: line.coordinates,
          direction: backendToFrontendDirection(
            line.direction ?? "both",
            line.coordinates
          ),
          preset: line.preset,
          color: LINE_COLORS[0],
        },
      ]
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { fontFamily: "Mulish" },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Mulish",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <span>Line Crossing</span>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: "text.primary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Typography sx={{ fontFamily: "Mulish", color: "error.main", py: 2 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && previewUrl && (
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <img
              src={previewUrl}
              onLoad={handleImageLoad}
              alt="Camera preview"
              style={{
                width: dimensions ? `${dimensions.width}px` : "auto",
                height: dimensions ? `${dimensions.height}px` : "auto",
                display: "block",
              }}
            />
            {dimensions && line && (
              <LineCrossingsOverlay
                lines={overlayLines}
                width={dimensions.width}
                height={dimensions.height}
              />
            )}
          </Box>
        )}
        {!loading && line && (
          <Box sx={{ fontFamily: "Mulish" }}>
            <Typography sx={{ fontFamily: "Mulish", mt: 2 }}>
              Direction:{" "}
              {DIRECTION_LABELS[normalizeBackendDirection(line.direction)]}
            </Typography>
            <Typography sx={{ fontFamily: "Mulish" }}>
              Configuration:{" "}
              {line.preset === null ? "Default" : `Preset ${line.preset}`}
            </Typography>
            <HistoryCoordinates value={line.coordinates} />
          </Box>
        )}
        {!loading && !line && (
          <Typography sx={{ fontFamily: "Mulish", mt: 2 }}>
            There was no line recorded for this change.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};
